import React, { RefObject, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store/store';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import ModalConsultorDre from '../../../../components/modal/contac/consultor-dre/ModalConsultorDre';
import FiltroModal from '../../../../components/modal/contac/filtro/ModalFiltro';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import '../../../../components/como-funciona/help-circle-btn.css';
import ModalReportsConsultor from '../../../../components/modal/contac/reports/ModalReportsConsultor';
import { fetchConsultoresResumoLista, fetchConsultorResumo, fetchContacStatus } from '../../../../api/contac/contacAnalyticsApi';
import {
  downloadRelatorioCsvDreConsultor,
  downloadRelatorioXlsxDreConsultor,
} from '../../../../api/contac/contacReportsConsultoresApi';
import { toast } from 'react-toastify';
import { notify } from '../../../../shared/feedback/notify';
import ArrowLeftOrange from '../../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../../img/arrow-right-gray.svg';
import ArrowDown from '../../../../img/arrow-down.svg';
import ArrowUp from '../../../../img/arrow-up.svg';
import { FixedSizeList as List } from 'react-window';
import Filter from '../../../../img/filter.svg';
import ContacDocumentOrange from '../../../../img/contac_document_orange.svg';
import { DismissableTip } from '../../../../components/tooltip/DismissableTip';
import './DashboardConsultor.css';
import '../../../../components/modal/contac/placa-dre/ModalPlacaDre.css';
import '../../../../components/modal/contac/categoria-dre/ModalCategoriaDre.css';

interface LinhaCompetencia {
  grupo: string | null;
  linha: string | null;
  competencia: string;
  total_entradas: number;
  total_saidas: number;
  classificacao: string | null;
}

const classBySign3 = (n?: number) =>
  typeof n === 'number' && n < 0 ? 'value-negative3' : 'value-positive3';

const formatCurrency = (valor: number | null | undefined) =>
  typeof valor === 'number'
    ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    : '0,00';

const formatPercent = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    : '—';

const pctOf = (signedValue: number, baseAbs: number) =>
  baseAbs === 0 ? null : (signedValue / baseAbs) * 100;

const toNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0)) || 0;

const sumGrupoTotal = (
  competencias: Record<string, LinhaCompetencia[]> | undefined,
  grupoAlvo: string,
  tipo: 'ENTRADA' | 'SAIDA',
) => {
  let total = 0;
  for (const linhas of Object.values(competencias ?? {})) {
    for (const l of linhas ?? []) {
      if (l?.grupo !== grupoAlvo) continue;
      if (l?.linha !== 'TOTAL') continue;
      total += tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
    }
  }
  return total;
};

const buildClassificacoes = (
  competencias: Record<string, LinhaCompetencia[]> | undefined,
  grupoAlvo: string,
  tipo: 'ENTRADA' | 'SAIDA',
) => {
  const acc: Record<string, { classificacao: string; total: number; porMes: Record<string, number> }> = {};
  for (const [mes, linhas] of Object.entries(competencias ?? {})) {
    for (const l of linhas ?? []) {
      if (l?.grupo !== grupoAlvo) continue;
      if (l?.linha !== 'CLASSIFICACAO') continue;
      const key = (l?.classificacao ?? '').trim() || 'Sem Classificação';
      const v = tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
      if (!acc[key]) acc[key] = { classificacao: key, total: 0, porMes: {} };
      acc[key].total += v;
      acc[key].porMes[mes] = (acc[key].porMes[mes] ?? 0) + v;
    }
  }
  return Object.values(acc)
    .map((x) => ({
      classificacao: x.classificacao,
      total: x.total,
      porMes: Object.entries(x.porMes).map(([competencia, valor]) => ({ competencia, valor })),
    }))
    .sort((a, b) => b.total - a.total);
};

const mapClassifs = (arr: any[] = []): { classificacao: string; total: number; detalhes: { mes: string; valor: number }[] }[] =>
  arr.map((c: any) => ({
    classificacao: c?.classificacao ?? 'Sem Classificação',
    total: toNum(c?.total),
    detalhes: (Array.isArray(c?.porMes) ? c.porMes : c?.detalhes || []).map((m: any) => ({
      mes: m?.competencia ?? m?.mes ?? '',
      valor: toNum(m?.valor),
    })),
  }));

interface ContacDashboardConsultorProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

const ContacDashboardConsultor: React.FC<ContacDashboardConsultorProps> = ({ sidebarRef }) => {
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<string | null>(null);
  const [statusIndisponivel, setStatusIndisponivel] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Sem o carimbo de atualizacao o usuario le dado antigo achando que e o mais recente:
    // o aviso fica na propria tela (permanente) alem do toast, que some em segundos.
    fetchContacStatus()
      .then((s) => setUltimaAtualizacao(s.ultimaAtualizacao))
      .catch(() => {
        setStatusIndisponivel(true);
        notify.warning('Não foi possível verificar a data da última atualização dos dados.', { toastId: 'contac-status' });
      });
  }, []);

  const { codigoVoluntario } = useSelector((s: RootState) => s.auth);
  const isConsultorUser = !!codigoVoluntario;

  const [dreResultado, setDreResultado] = useState<any | null>(null);
  const [dreExpanded, setDreExpanded] = useState<string[]>([]);
  const [dreDownloading, setDreDownloading] = useState(false);

  const toggleExpandDre = (key: string) =>
    setDreExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const [, setListWidth] = useState(0);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const headerScrollRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  React.useLayoutEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => setListWidth(el.clientWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const [dataConsultoresTodos, setDataConsultoresTodos] = useState<
    { name: string; codigo_voluntario: number; receitaTotal: number; saidaTotal: number; recorrente: number; Resultado: number }[]
  >([]);

  const [sortColumn, setSortColumn] = useState<'name' | 'receitaTotal' | 'saidaTotal' | 'recorrente' | 'Resultado' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const defaultDate = new Date();
  defaultDate.setDate(1);
  defaultDate.setMonth(defaultDate.getMonth() - 2);
  const defaultYear = defaultDate.getFullYear();
  const defaultMonth = defaultDate.getMonth() + 1;

  const PAGE_SIZE_OPTIONS = [10, 20, 30, 50] as const;
  const PAGE_SIZE_ALL = 600;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE_ALL);
  const [limitUi, setLimitUi] = useState(PAGE_SIZE_ALL);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [hasFiltered, setHasFiltered] = useState(false);

  const [periodoSelecionado, setPeriodoSelecionado] = useState<{ ini: string; fim: string } | null>(null);

  const [searchConsultor, setSearchConsultor] = useState('');
  const [searchConsultorAplicado, setSearchConsultorAplicado] = useState('');

  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: isConsultorUser ? (
        <p>
          Esta é a <strong>sua carteira</strong>: as placas ligadas ao seu código de consultor e o que
          elas movimentaram no período. Entradas é a receita gerada, Saídas é o custo e Resultado é a
          diferença entre os dois. Você vê apenas os seus dados — o próprio sistema faz esse recorte.
        </p>
      ) : (
        <p>
          Cada linha é um <strong>consultor</strong> e o que a carteira dele movimentou no período:
          Entradas (receita gerada), Saídas (custo) e Resultado (a diferença entre os dois). Os dados
          vêm do sistema financeiro já consolidados por mês.
        </p>
      ),
    },
    {
      titulo: 'Período de análise',
      conteudo: (
        <>
          <p>
            O período é escolhido por <strong>mês e ano de início e fim</strong>, no botão de filtros,
            e aparece ao lado do título da tela.
          </p>
          <p>
            A consulta começa em <strong>janeiro de 2025</strong> e vai até o
            <strong> fechamento de dois meses atrás</strong>: o mês corrente e o anterior ainda não
            estão consolidados e por isso não podem ser selecionados.
          </p>
        </>
      ),
    },
    ...(isConsultorUser
      ? []
      : [
          {
            titulo: 'Encontrar um consultor',
            conteudo: (
              <p>
                Dentro dos filtros existe o campo <strong>Nome do Consultor</strong>: digite parte do
                nome e aplique para restringir a lista. Clicar em um consultor abre o detalhamento da
                carteira dele, de onde também saem os relatórios em CSV e Excel.
              </p>
            ),
          },
        ]),
  ];

  const [startYear, setStartYear] = useState<number>(defaultYear);
  const [startMonth, setStartMonth] = useState<number>(defaultMonth);
  const [endYear, setEndYear] = useState<number>(defaultYear);
  const [endMonth, setEndMonth] = useState<number>(defaultMonth);

  const [modalOpen, setModalOpen] = useState(false);
  const [consultorSelecionado, setConsultorSelecionado] = useState<{ nome: string, codigo: number } | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalReportsConsultorAberto, setModalReportsConsultorAberto] = useState(false);

  const formatPeriodo = (ini: string, fim: string): string => {
    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    const [yi, mi] = ini.split('-').map(Number);
    const [yf, mf] = fim.split('-').map(Number);
    return `${meses[mi - 1]}/${yi} – ${meses[mf - 1]}/${yf}`;
  };

  const periodoExibido =
    hasFiltered && periodoSelecionado ? formatPeriodo(periodoSelecionado.ini, periodoSelecionado.fim) : '';

  const applyFilters = (sY: number, sM: number, eY: number, eM: number) => {
    const dataInicio = new Date(sY, sM - 1, 1).toISOString().split('T')[0];
    const dataFim = new Date(eY, eM, 0).toISOString().split('T')[0];

    setLimit(limitUi);
    setPage(1);
    setHasFiltered(true);
    setPeriodoSelecionado({ ini: dataInicio, fim: dataFim });
    loadData(dataInicio, dataFim, 1, limitUi);

    setStartYear(sY); setStartMonth(sM);
    setEndYear(eY); setEndMonth(eM);
  };

  React.useEffect(() => {
    if (!hasFiltered || !periodoSelecionado) return;
    loadData(periodoSelecionado.ini, periodoSelecionado.fim, page, limit);
    // Este efeito existe só para reagir à troca de página e de itens por página.
    // A troca de período já chama loadData direto em applyFilters: incluir
    // periodoSelecionado aqui faria a mesma consulta rodar duas vezes, e incluir
    // loadData (recriada a cada render) faria a consulta rodar em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  React.useEffect(() => {
    if (isConsultorUser && !hasFiltered) {
      applyFilters(startYear, startMonth, endYear, endMonth);
    }
    // Carga inicial da carteira do próprio consultor: deve rodar uma única vez,
    // quando se sabe que o usuário é consultor. As datas e o applyFilters são
    // recriados a cada render; incluí-los faria o efeito refazer a consulta a
    // cada mudança de filtro, atropelando o período escolhido pelo usuário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsultorUser]);

  const [dataResultado, setDataResultado] = useState<{
    totalReceita: number;
    totalSaidas: number;
    despesaRecorrenteTotal: number;
    resultado: number;
  } | null>(null);

  const handleSort = (column: typeof sortColumn) => {
    if (!column) return;

    let newDirection: 'asc' | 'desc' = 'desc';
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    setSortColumn(column);
    setSortDirection(newDirection);

    const sorted = [...dataConsultoresTodos].sort((a, b) => {
      const valA = a[column];
      const valB = b[column];
      if (typeof valA === 'number' && typeof valB === 'number') {
        return newDirection === 'asc' ? valA - valB : valB - valA;
      }
      if (typeof valA === 'string' && typeof valB === 'string') {
        return newDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return 0;
    });

    setDataConsultoresTodos(sorted);
  };

  const formatarValor = (valor: number) => {
    const formatado = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
    return valor < 0 ? `R$ -${formatado.replace('R$', '').trim()}` : formatado;
  };

  const handleOpenReportsConsultor = () => {
    if (!hasFiltered) {
      toast.error('Antes selecione um período nos filtros');
      return;
    }
    setModalReportsConsultorAberto(true);
  };

  const handleDownloadCsvConsultor = async () => {
    try {
      setDreDownloading(true);
      const ymd = (y: number, m: number, d: number) =>
        `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dataInicio = periodoSelecionado?.ini ?? ymd(startYear, startMonth, 1);
      const dataFim = periodoSelecionado?.fim ?? ymd(endYear, endMonth, new Date(endYear, endMonth, 0).getDate());

      await downloadRelatorioCsvDreConsultor({
        dataInicio,
        dataFim,
        consultores: [String(codigoVoluntario)],
      });
      toast.success('Relatório CSV gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar CSV');
    } finally {
      setDreDownloading(false);
    }
  };

  const handleDownloadXlsxConsultor = async () => {
    try {
      setDreDownloading(true);
      const ymd = (y: number, m: number, d: number) =>
        `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dataInicio = periodoSelecionado?.ini ?? ymd(startYear, startMonth, 1);
      const dataFim = periodoSelecionado?.fim ?? ymd(endYear, endMonth, new Date(endYear, endMonth, 0).getDate());

      await downloadRelatorioXlsxDreConsultor({
        dataInicio,
        dataFim,
        consultores: [String(codigoVoluntario)],
      });
      toast.success('Relatório XLSX gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar XLSX');
    } finally {
      setDreDownloading(false);
    }
  };

  const extraiCodigo = (label?: string) => {
    const m = String(label ?? '').trim().match(/^(\d+)/);
    return m ? Number(m[1]) : undefined;
  };

  const loadData = async (
    dataInicio: string,
    dataFim: string,
    pageArg = page,
    limitArg = limit,
    searchArg = searchConsultorAplicado,
  ) => {
    try {
      setLoading(true);

      if (isConsultorUser) {
        const resp: any = await fetchConsultorResumo({
          dataInicio,
          dataFim,
          codigo_consultor: String(codigoVoluntario),
        });

        const item =
          resp?.item ??
          (Array.isArray(resp?.items) ? resp.items[0] : null) ??
          resp ??
          null;

        if (!item) {
          setDreResultado(null);
          return;
        }

        const i = item || {};
        const d = i.detalhamento || {};
        const competencias = (i.competencias ?? d.competencias) as
          | Record<string, LinhaCompetencia[]>
          | undefined;

        const receitaSga = toNum(d.receitaMensalidade);
        const receitaNasajon = toNum(d.receitaNasajonComPlaca);
        const rateioEntrada = toNum(d.receitaRateio);

        const saidaNasajon = toNum(d.despesaNasajonComPlaca);
        const saidaEasy = toNum(d.despesaEasy);
        const rateioSaida = toNum(d.despesaRateio) + toNum(d.despesaEasyExcedente);
        const saidaRecorrente = toNum(d.despesaRecorrente);
        const saidaRecorrenteExcedente = toNum(d.despesaRecorrenteExcedente);

        const receitaOperacionalLegacy = receitaSga + receitaNasajon;
        const outrasReceitasLegacy = rateioEntrada;

        const receitaOperacional = competencias
          ? sumGrupoTotal(competencias, 'RECEITA OPERACIONAL', 'ENTRADA')
          : receitaOperacionalLegacy;

        const outrasReceitas = competencias
          ? sumGrupoTotal(competencias, 'OUTRAS RECEITAS', 'ENTRADA')
          : outrasReceitasLegacy;

        const receitaBruta = competencias
          ? toNum(i.totaisEntrada ?? i.receitaBruta ?? (receitaOperacional + outrasReceitas))
          : receitaOperacional + outrasReceitas;

        const despesasOperacionaisLegacy = saidaNasajon + saidaEasy + saidaRecorrente + saidaRecorrenteExcedente;

        const despesasOperacionais = competencias
          ? sumGrupoTotal(competencias, 'DESPESAS OPERACIONAIS', 'SAIDA')
          : despesasOperacionaisLegacy;

        const margemContribuicao = competencias
          ? sumGrupoTotal(competencias, 'MARGEM DE CONTRIBUIÇÃO', 'SAIDA')
          : rateioSaida;

        const resultadoOperacional = receitaBruta - despesasOperacionais;
        const resultadoFinal = toNum(i.resultado ?? d.resultado ?? (resultadoOperacional - margemContribuicao));

        const entradasOp = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'RECEITA OPERACIONAL', 'ENTRADA'))
          : mapClassifs(d.classificacoesEntradaPlaca ?? i.classificacoesEntradaPlaca ?? []);

        const saidasOpBase = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'DESPESAS OPERACIONAIS', 'SAIDA'))
          : mapClassifs(d.classificacoesSaidaPlaca ?? i.classificacoesSaidaPlaca ?? []);

        const alreadyHasExcedente = saidasOpBase.some((x) =>
          String(x.classificacao).toLowerCase().includes('recorrente excedente'),
        );

        const saidasOp = [
          ...saidasOpBase,
          ...(saidaRecorrenteExcedente && !alreadyHasExcedente
            ? [
              {
                classificacao: 'Despesa Recorrente Excedente',
                total: saidaRecorrenteExcedente,
                detalhes: [{ mes: 'Período', valor: saidaRecorrenteExcedente }],
              },
            ]
            : []),
        ];

        const entradasRateio = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'OUTRAS RECEITAS', 'ENTRADA'))
          : mapClassifs(d.classificacoesEntradaRateio ?? i.classificacoesEntradaRateio ?? []);

        const saidasRateio = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'MARGEM DE CONTRIBUIÇÃO', 'SAIDA'))
          : mapClassifs(d.classificacoesSaidaRateio ?? i.classificacoesSaidaRateio ?? []);

        setDreResultado({
          consultor: i.consultor ?? i.consultor_nome ?? i.nome_consultor ?? '',
          codigoVoluntario: String(i.codigoVoluntario ?? i.codigo_consultor ?? codigoVoluntario),
          receitaBruta,
          receitaOperacional,
          outrasReceitas,
          despesasOperacionais,
          margemContribuicao,
          resultadoOperacional,
          resultadoFinal,
          placasAtivas: toNum(i.placasAtivas),
          placasPeriodo: toNum(i.placasPeriodo),
          entradasClassificacoes: entradasOp,
          saidasClassificacoes: saidasOp,
          entradasClassificacoesRateio: entradasRateio,
          saidasClassificacoesRateio: saidasRateio,
        });

      } else {
        const resp = await fetchConsultoresResumoLista({
          dataInicio,
          dataFim,
          order: 'lucro',
          page: pageArg,
          limit: limitArg,
          ...(searchArg ? { consultor: searchArg } : {}),
        });

        const num = (x: any) => (typeof x === 'number' ? x : Number(x ?? 0)) || 0;

        const items = Array.isArray(resp?.items) ? resp.items : [];

        const totalReceita = items.reduce((acc: number, it: any) => acc + num(it?.totaisEntrada), 0);
        const totalSaidas = items.reduce((acc: number, it: any) => acc + num(it?.totaisSaida), 0);
        const resultado = items.reduce((acc: number, it: any) => acc + num(it?.resultado), 0);

        const recorrenteTotal = items.reduce((acc: number, it: any) => {
          const competencias = it?.competencias && typeof it.competencias === 'object' ? it.competencias : {};
          const linhas = Object.values(competencias).flatMap((v: any) => (Array.isArray(v) ? v : []));
          const recorrente = linhas
            .filter((l: any) =>
              l?.linha === 'CLASSIFICACAO' &&
              String(l?.classificacao ?? '').trim().toUpperCase() === 'RECORRENTE'
            )
            .reduce((s: number, l: any) => s + num(l?.total_saidas), 0);

          return acc + recorrente;
        }, 0);

        setDataResultado({
          totalReceita,
          totalSaidas,
          despesaRecorrenteTotal: recorrenteTotal,
          resultado,
        });

        const consultores = items.map((it: any) => {
          const competencias = it?.competencias && typeof it.competencias === 'object' ? it.competencias : {};
          const linhas = Object.values(competencias).flatMap((v: any) => (Array.isArray(v) ? v : []));
          const recorrente = linhas
            .filter((l: any) =>
              l?.linha === 'CLASSIFICACAO' &&
              String(l?.classificacao ?? '').trim().toUpperCase() === 'RECORRENTE'
            )
            .reduce((s: number, l: any) => s + num(l?.total_saidas), 0);

          return {
            name: it?.consultor || 'Sem nome',
            codigo_voluntario: Number(it?.consultor_codigo) || 0,
            receitaTotal: num(it?.totaisEntrada),
            saidaTotal: num(it?.totaisSaida),
            recorrente,
            Resultado: num(it?.resultado),
          };
        });

        setDataConsultoresTodos(consultores);

        const totalItems = Number(resp?.total) || 0;
        const totalPages = Math.max(1, Math.ceil(totalItems / (limitArg || 1)));

        setTotalPages(totalPages);
        setTotalItems(totalItems);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      // at-65: `err.message` do axios é o status cru; a explicação vem no corpo.
      notify.errorFrom(
        error,
        'Não foi possível carregar os dados dos consultores. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      // at-65 (BUG-V79): o par console.time/timeEnd('render') era instrumentação de
      // desenvolvimento esquecida em arquivo de produção (regra "sem console.*").
      setLoading(false);
    }
  };

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  if (loading || dreDownloading) return <FullScreenLoader />;

  return (
    <div className="Frame1000004012 dashboard-consultor-page">
      <div className="ConteDo">
        {/* Título */}
        <div className="TTuloRecepcao">
          <div className="ChefeDeOficina">
            {isConsultorUser ? 'Contac - Sua Carteira' : 'Contac - Dashboard de Consultores'}
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
              style={{ marginLeft: 8, verticalAlign: 'middle' }}
            >
              ?
            </button>
            {periodoExibido && <span className="PeriodoTitulo"> {periodoExibido}</span>}
          </div>
          {ultimaAtualizacao ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-3, #888)', margin: '4px 0 0 0' }}>
              Dados atualizados em: {new Date(ultimaAtualizacao).toLocaleString('pt-BR')}
            </p>
          ) : statusIndisponivel ? (
            <p style={{ fontSize: 12, color: 'var(--tema-texto-laranja, #b26a00)', margin: '4px 0 0 0' }}>
              Não foi possível verificar a data da última atualização dos dados.
            </p>
          ) : null}
        </div>

        <div className="VisualizarOrdenar">
          <div
            className="Frame1000004037"
            style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}
          >
            <DismissableTip
              tipId="contac_consultor_filtros"
              content="Selecione o período desejado para filtrar os dados dos consultores."
              position="bottom"
            >
              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    src={Filter}
                    alt="Filtro"
                    width="19.80"
                    height="19.80"
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ComponentName">Filtros</div>
                </div>
              </div>
            </DismissableTip>

            <DismissableTip
              tipId="contac_consultor_relatorios"
              content="Gere relatórios ou exportações de dados dos consultores."
              position="bottom"
            >
              <div
                className="CtaBig MouseHover"
                onClick={isConsultorUser ? handleDownloadCsvConsultor : handleOpenReportsConsultor}
                style={{
                  opacity: dreDownloading ? 0.6 : 1,
                  pointerEvents: dreDownloading ? 'none' : 'auto',
                }}
              >
                <div className="Frame1000003987">
                  <img
                    src={ContacDocumentOrange}
                    alt="CSV"
                    width="19.80"
                    height="19.80"
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ComponentName">
                    {isConsultorUser
                      ? dreDownloading
                        ? 'Gerando...'
                        : 'Gerar CSV'
                      : 'Relatórios'}
                  </div>
                </div>
              </div>
            </DismissableTip>

            {isConsultorUser && (
              <div
                className="CtaBig MouseHover"
                onClick={handleDownloadXlsxConsultor}
                style={{
                  opacity: dreDownloading ? 0.6 : 1,
                  pointerEvents: dreDownloading ? 'none' : 'auto',
                }}
              >
                <div className="Frame1000003987">
                  <img
                    src={ContacDocumentOrange}
                    alt="XLSX"
                    width="19.80"
                    height="19.80"
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ComponentName">
                    {dreDownloading ? 'Gerando...' : 'Gerar XLSX'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ====== Bloco de Resumo Financeiro (acima da tabela) ====== */}
        {!isConsultorUser && dataResultado && (
          <div className="container-superior-consultor">
            <div className="Frame1-consultor">
              {/* Entradas */}
              <div className="card-financeiro-consultor">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_arrow_down.svg').default} alt="Seta para baixo" />
                </div>
                <div style={{ width: 100 }}>
                  <div className="card-titulo">Entradas</div>
                  <div className="card-valor">{formatarValor(dataResultado?.totalReceita ?? 0)}</div>
                </div>
              </div>

              {/* Saídas */}
              <div className="card-financeiro-consultor">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_arrow_up.svg').default} alt="Seta para cima" />
                </div>
                <div style={{ width: 100 }}>
                  <div className="card-titulo">Saídas</div>
                  <div className="card-valor">{formatarValor(dataResultado?.totalSaidas ?? 0)}</div>
                </div>
              </div>

              {/* Resultado */}
              <div className="card-financeiro-consultor resultado">
                <div className="card-icone">
                  <img style={{ width: 48, height: 48 }} src={require('../../../../img/contac_line.svg').default} alt="Linha" />
                </div>
                <div style={{ width: 100 }}>
                  <div className="card-titulo">Resultado</div>
                  <div
                    className="card-valor"
                    style={{
                      color:
                        (dataResultado?.resultado ?? 0) > 0
                          ? 'green'
                          : (dataResultado?.resultado ?? 0) < 0
                            ? 'red'
                            : 'black',
                    }}
                  >
                    {formatarValor(dataResultado?.resultado ?? 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isConsultorUser ? (
          <div className="inline-dre-body">
            {!dreResultado ? (
              <div className="card">
                <div className="card-entry">Nenhum dado encontrado para este consultor nesse período.</div>
              </div>
            ) : (
              <>
                {/* Card 1 - Suas Informações */}
                <div className="card card-left">
                  <div className="modal-header">Suas informações</div>
                  <div className="info-row">
                    <span className="info-label">Consultor</span>
                    <span className="info-value">{dreResultado.consultor}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Código</span>
                    <span className="info-value">{String(dreResultado.codigoVoluntario || codigoVoluntario)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Período</span>
                    <span className="info-value">
                      {periodoSelecionado ? formatPeriodo(periodoSelecionado.ini, periodoSelecionado.fim) : '—'}
                    </span>
                  </div>
                </div>

                {/* Card 2 - DRE */}
                <div className="card">
                  <div className="modal-header">Demonstrativo de Resultados (DRE)</div>

                  <div className="row-between dre-columns-header" style={{ marginBottom: 12, borderBottom: '1px dashed var(--tema-linha, #eef2f5)', paddingBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                        Placas Ativas: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(dreResultado?.placasAtivas ?? 0)}</strong>
                      </span>
                      <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                        Placas do Período: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(dreResultado?.placasPeriodo ?? 0)}</strong>
                      </span>
                    </div>
                    <div className="dre-value-pct">
                      <span className="dre-col-header" style={{ minWidth: 140, textAlign: 'right' }}>Valor</span>
                      <span className="dre-col-header" style={{ minWidth: 80, textAlign: 'right' }}>% Rec. Bruta</span>
                    </div>
                  </div>

                  {/* Receita Bruta */}
                  <div className="row-between">
                    <div className="dre-title">
                      Receita Bruta <span className="dre-note">(Todas as Receitas)</span>
                    </div>
                    <div className="dre-value-pct">
                      <span className="entry-text value-positive3">R$ {formatCurrency(dreResultado?.receitaBruta)}</span>
                      <span className="dre-percent value-positive5">100,00%</span>
                    </div>
                  </div>

                  {/* Receita Operacional */}
                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpandDre('op')}
                    aria-expanded={dreExpanded.includes('op')}
                  >
                    <div className="dre-title">
                      Receita Operacional <span className="dre-note">(Receitas ligadas às placas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={dreExpanded.includes('op') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    <div className="dre-value-pct">
                      <span className="entry-text value-positive3">R$ {formatCurrency(dreResultado?.receitaOperacional)}</span>
                      <span className="dre-percent value-positive5">
                        {formatPercent(pctOf(Math.abs(Number(dreResultado?.receitaOperacional) || 0), Math.abs(Number(dreResultado?.receitaBruta) || 0)))}
                      </span>
                    </div>
                  </div>

                  {dreExpanded.includes('op') && (
                    <div className="subclass-container">
                      {(dreResultado?.entradasClassificacoes || []).map((ent: any, idx: number) => (
                        <React.Fragment key={`ent-op-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{ent.classificacao}</span>
                              <img
                                src={dreExpanded.includes(`ent-op-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpandDre(`ent-op-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-positive4">R$ {formatCurrency(ent.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {dreExpanded.includes(`ent-op-${idx}`) &&
                            (ent.detalhes || []).map((det: any, dIdx: number) => (
                              <div className="detail-row" key={`ent-op-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Outras Receitas */}
                  <div className="row-between">
                    <div className="dre-title">
                      Outras Receitas <span className="dre-note">(Receitas não ligadas às placas)</span>
                    </div>
                    <div className="dre-value-pct">
                      <span className="entry-text value-positive3">R$ {formatCurrency(dreResultado?.outrasReceitas)}</span>
                      <span className="dre-percent value-positive5">
                        {formatPercent(pctOf(Math.abs(Number(dreResultado?.outrasReceitas) || 0), Math.abs(Number(dreResultado?.receitaBruta) || 0)))}
                      </span>
                    </div>
                  </div>

                  {/* Despesas Operacionais */}
                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpandDre('co')}
                    aria-expanded={dreExpanded.includes('co')}
                  >
                    <div className="dre-title">
                      Despesas Operacionais <span className="dre-note">(Despesas ligadas às placas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={dreExpanded.includes('co') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    <div className="dre-value-pct">
                      <span className="entry-text value-negative3">
                        R$ -{formatCurrency(dreResultado?.despesasOperacionais)}
                      </span>
                      <span className="dre-percent value-negative5">
                        {formatPercent(pctOf(-Math.abs(Number(dreResultado?.despesasOperacionais) || 0), Math.abs(Number(dreResultado?.receitaBruta) || 0)))}
                      </span>
                    </div>
                  </div>

                  {dreExpanded.includes('co') && (
                    <div className="subclass-container">
                      {(dreResultado?.saidasClassificacoes || []).map((sai: any, idx: number) => (
                        <React.Fragment key={`sai-dir-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{sai.classificacao}</span>
                              <img
                                src={dreExpanded.includes(`sai-dir-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpandDre(`sai-dir-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-negative4">R$ {formatCurrency(sai.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {dreExpanded.includes(`sai-dir-${idx}`) &&
                            (sai.detalhes || []).map((det: any, dIdx: number) => (
                              <div className="detail-row" key={`sai-dir-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Resultado Operacional */}
                  <div className="row-between">
                    <div className="dre-title">
                      Resultado Operacional <span className="dre-note">(Receita Bruta - Desp. Operacional)</span>
                    </div>
                    <div className="dre-value-pct">
                      <span className={`entry-text ${classBySign3(dreResultado?.resultadoOperacional)}`}>
                        R$ {formatCurrency(dreResultado?.resultadoOperacional)}
                      </span>
                      <span className={`dre-percent ${(Number(dreResultado?.resultadoOperacional) || 0) < 0 ? 'value-negative5' : 'value-positive5'}`}>
                        {formatPercent(pctOf(Number(dreResultado?.resultadoOperacional) || 0, Math.abs(Number(dreResultado?.receitaBruta) || 0)))}
                      </span>
                    </div>
                  </div>

                  {/* Margem de Contribuição */}
                  <div className="row-between">
                    <div className="dre-title">
                      Margem de Contribuição <span className="dre-note">(Rateio de despesas não ligadas às placas)</span>
                    </div>
                    <div className="dre-value-pct">
                      <span className="entry-text value-negative3">
                        R$ -{formatCurrency(dreResultado?.margemContribuicao)}
                      </span>
                      <span className="dre-percent value-negative5">
                        {formatPercent(pctOf(-Math.abs(Number(dreResultado?.margemContribuicao) || 0), Math.abs(Number(dreResultado?.receitaBruta) || 0)))}
                      </span>
                    </div>
                  </div>

                  {/* Resultado Final */}
                  <div className="row-between" style={{ borderTop: '2px solid #EA5D0E', paddingTop: '20px', marginTop: '16px' }}>
                    <div className="dre-title" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      Resultado <span className="dre-note">(Resultado Operac. - Margem)</span>
                    </div>
                    <div className="dre-value-pct">
                      <span className={`entry-text ${classBySign3(dreResultado?.resultadoFinal)}`} style={{ fontSize: '20px' }}>
                        R$ {formatCurrency(dreResultado?.resultadoFinal)}
                      </span>
                      <span className={`dre-percent ${(Number(dreResultado?.resultadoFinal) || 0) < 0 ? 'value-negative5' : 'value-positive5'}`} style={{ fontSize: '16px' }}>
                        {formatPercent(pctOf(Number(dreResultado?.resultadoFinal) || 0, Math.abs(Number(dreResultado?.receitaBruta) || 0)))}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className='ContacTable'>
            <div
              style={{
                display: 'flex',
                gap: 20,
                flexWrap: 'wrap',
                alignItems: 'center',
                paddingLeft: 30,
                paddingBottom: 0,
              }}
            >
              <div className="CtaBigContac2">
                <div className="Frame1000003987">
                  <div className="ComponentNameContac">Consultores por página:</div>
                  <select
                    value={limitUi}
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      setLimitUi(newLimit);
                      setLimit(newLimit);
                      setPage(1);
                    }}
                    className="ComponentNameImgIcon"
                  >
                    {PAGE_SIZE_OPTIONS.map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                    <option key="all" value={PAGE_SIZE_ALL}>Todos</option>
                  </select>
                </div>
              </div>


            </div>

            <div className="TabelaHeaderScroll" ref={headerScrollRef}>
              <div className="Tabela Tabela--consultores Tabela--header">
                <div className="Item">
                  <DismissableTip
                    tipId="contac_consultor_tabela_dre"
                    content="Clique no nome de um consultor para abrir a Demonstração de Resultado (DRE) detalhada dele."
                    position="top"
                    disabled={dataConsultoresTodos.length === 0}
                  >
                    <div className="CabecalhoData" onClick={() => handleSort('name')}>
                      Consultor
                    </div>
                  </DismissableTip>
                </div>
                <div className="Item">
                  <div className="CabecalhoData" onClick={() => handleSort('receitaTotal')}>
                    Entradas
                    {sortColumn === 'receitaTotal' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </div>
                <div className="Item">
                  <div className="CabecalhoData" onClick={() => handleSort('saidaTotal')}>
                    Saídas
                    {sortColumn === 'saidaTotal' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </div>
                <div className="Item">
                  <div className="CabecalhoData" onClick={() => handleSort('recorrente')}>
                    Recorrente
                    {sortColumn === 'recorrente' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </div>
                <div className="Item">
                  <div className="CabecalhoData" onClick={() => handleSort('Resultado')}>
                    Resultado
                    {sortColumn === 'Resultado' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </div>
              </div>
            </div>

            <div className="TabelaScroll" ref={tableContainerRef} onScroll={handleScroll}>

              <List
                height={Math.max(48, 48 * dataConsultoresTodos.length)}
                itemCount={dataConsultoresTodos.length}
                itemSize={48}
                width="100%"
              >
                {({ index, style }) => {
                  const item = dataConsultoresTodos[index];
                  return (
                    <div className="Tabela Tabela--consultores" style={style} key={item.codigo_voluntario || index}>
                      <div className="Item">
                        <div
                          className="Data link"
                          style={{ cursor: 'pointer', color: '#EA5D0E', textDecoration: 'underline' }}
                          onClick={() => {
                            const codigo = item.codigo_voluntario || extraiCodigo(item.name);
                            if (!codigo) return toast.warn('Código do consultor não disponível neste agregado.');
                            setConsultorSelecionado({ nome: item.name, codigo });
                            setModalOpen(true);
                          }}
                        >
                          {item.name}
                        </div>
                      </div>
                      <div className="Item"><div className="Data">{formatarValor(item.receitaTotal)}</div></div>
                      <div className="Item"><div className="Data">{formatarValor(item.saidaTotal)}</div></div>
                      <div className="Item"><div className="Data">{formatarValor(item.recorrente)}</div></div>
                      <div className="Item">
                        <div
                          className="Data"
                          style={{
                            color: item.Resultado > 0 ? 'green' : item.Resultado < 0 ? 'red' : 'var(--tema-texto, black)',
                          }}
                        >
                          {formatarValor(item.Resultado)}
                        </div>
                      </div>
                    </div>
                  );
                }}
              </List>
            </div>

            {/* Paginação */}
            <div className="PaginaO">
              {/* Botão Anterior */}
              <div
                className={`Frame1000003950 ${page === 1 ? 'disabled' : ''}`}
                onClick={() => { if (page > 1) setPage(page - 1); }}
                style={{ cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                <img
                  className="imgIcon"
                  src={page === 1 ? ArrowLeftGray : ArrowLeftOrange}
                  alt="Seta para Esquerda"
                  width="24"
                  height="24"
                />
                <div className="Anterior" style={{ color: page === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                  Anterior
                </div>
              </div>

              {/* Números das páginas */}
              <div className="Group21">
                <div className="Group20">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 10;

                    pages.push(
                      <div
                        key={1}
                        className={`page-number ${page === 1 ? 'active' : ''}`}
                        onClick={() => setPage(1)}
                      >
                        1
                        {page === 1 && <div className="Ellipse14" />}
                      </div>
                    );

                    if (page > maxVisiblePages - 4) {
                      pages.push(<span key="left-ellipsis" className="ellipsis">...</span>);
                    }

                    const startPage = Math.max(
                      2,
                      Math.min(
                        page - Math.floor((maxVisiblePages - 2) / 2),
                        totalPages - (maxVisiblePages - 2)
                      )
                    );
                    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <div
                          key={i}
                          className={`page-number ${page === i ? 'active' : ''}`}
                          onClick={() => setPage(i)}
                        >
                          {i}
                          {page === i && <div className="Ellipse14" />}
                        </div>
                      );
                    }

                    if (endPage < totalPages - 1) {
                      pages.push(<span key="right-ellipsis" className="ellipsis">...</span>);
                    }

                    if (totalPages > 1) {
                      pages.push(
                        <div
                          key={totalPages}
                          className={`page-number ${page === totalPages ? 'active' : ''}`}
                          onClick={() => setPage(totalPages)}
                        >
                          {totalPages}
                          {page === totalPages && <div className="Ellipse14" />}
                        </div>
                      );
                    }

                    return pages;
                  })()}

                  <div style={{ minWidth: '100%', fontSize: '14px' }}>de {totalItems} consultores</div>
                </div>
              </div>

              {/* Botão Próxima */}
              <div
                className={`Frame1000003949 ${page === totalPages ? 'disabled' : ''}`}
                onClick={() => { if (page < totalPages) setPage(page + 1); }}
                style={{ cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                <div className="PrXima" style={{ color: page === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                  Próxima
                </div>
                <img
                  className="imgIcon"
                  src={page === totalPages ? ArrowRightGray : ArrowRightOrange}
                  alt="Seta para Direita"
                  width="24"
                  height="24"
                />
              </div>
            </div>
          </div>
        )}

        {modalOpen && consultorSelecionado && (
          <ModalConsultorDre
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            codigoVoluntario={consultorSelecionado.codigo}
            nomeVoluntario={consultorSelecionado.nome}
            dataInicio={new Date(startYear, startMonth - 1, 1).toISOString().split('T')[0]}
            dataFim={new Date(endYear, endMonth, 0).toISOString().split('T')[0]}
          />
        )}
        <FiltroModal
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          sidebarRef={sidebarRef}
          onApplyFilters={(sY, sM, eY, eM, searchConsultorVal) => {
            setStartYear(sY);
            setStartMonth(sM);
            setEndYear(eY);
            setEndMonth(eM);
            if (searchConsultorVal !== undefined) {
              setSearchConsultor(searchConsultorVal);
              setSearchConsultorAplicado(searchConsultorVal);
            }

            const ymd = (y: number, m: number, d: number) =>
              `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

            const dataInicio = ymd(sY, sM, 1);
            const dataFim = ymd(eY, eM, new Date(eY, eM, 0).getDate());

            setLimit(limitUi);
            setPage(1);
            setHasFiltered(true);
            setPeriodoSelecionado({ ini: dataInicio, fim: dataFim });
            loadData(dataInicio, dataFim, 1, limitUi, searchConsultorVal !== undefined ? searchConsultorVal : searchConsultorAplicado);
          }}
          initialStartYear={startYear}
          initialStartMonth={startMonth}
          initialEndYear={endYear}
          initialEndMonth={endMonth}
          showSearchConsultor={!isConsultorUser}
          initialSearchConsultor={searchConsultor}
        />
        {modalReportsConsultorAberto && (
          <ModalReportsConsultor
            isOpen={modalReportsConsultorAberto}
            onClose={() => setModalReportsConsultorAberto(false)}
            dataInicio={periodoSelecionado?.ini ?? ''}
            dataFim={periodoSelecionado?.fim ?? ''}
          />
        )}

        <ComoFunciona
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          titulo={isConsultorUser ? 'Como Funciona — Sua Carteira' : 'Como Funciona — Dashboard de Consultores'}
          secoes={helpSections}
        />
      </div>
    </div>
  );
};

export default ContacDashboardConsultor;
